import React, { useEffect, useState } from "react";
import { Check, Users, ArrowLeft } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const Catering = () => {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [zelle, setZelle] = useState("");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", headcount: "",
    event_type: "", event_date: "", preferred_datetime: "", fulfillment_type: "pickup",
    food_selections: "", special_requests: "",
  });

  useEffect(() => {
    client.get("/catering/packages").then((r) => { setPackages(r.data.packages); setZelle(r.data.zelle_recipient); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, first_name: user.first_name, last_name: user.last_name, email: user.email, phone: user.phone || "" }));
  }, [user]);

  const open = (pkg) => {
    setSelected(pkg);
    setForm((f) => ({ ...f, headcount: pkg.people ? String(pkg.people) : "" }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error("Please add your name and email"); return; }
    setSubmitting(true);
    try {
      await client.post("/catering/request", {
        package_id: selected.id, ...form,
        headcount: form.headcount ? parseInt(form.headcount, 10) : null,
      });
      toast.success("Catering request sent!", { description: "We'll contact you to confirm details and payment." });
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (selected) {
    return (
      <div className="max-w-xl mx-auto px-4 pt-20 pb-24">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-[#9a4a2f] font-medium mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-heading text-3xl font-bold text-[#2c1e12]">{selected.name}</h1>
        <div className="mt-3 rounded-2xl bg-[#f7e4df] p-4">
          <p className="font-heading text-xl font-bold text-[#9a4a2f]">{selected.custom ? "Custom quote" : `$${selected.price.toFixed(2)}`}</p>
          <p className="text-sm text-[#6b5844] mt-1">Reservation deposit ${selected.deposit.toFixed(2)} via Zelle ({zelle}) to hold your date.</p>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-[#2c1e12] mb-2">Your details</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" value={form.first_name} onChange={set("first_name")} />
              <Field label="Last name" value={form.last_name} onChange={set("last_name")} />
            </div>
            <Field label="Email" type="email" value={form.email} onChange={set("email")} />
            <Field label="Phone number" value={form.phone} onChange={set("phone")} />
          </div>

          <div>
            <h3 className="font-heading text-lg font-bold text-[#2c1e12] mb-2">Event details</h3>
            <Field label="Number of people" type="number" value={form.headcount} onChange={set("headcount")} />
            <Field label="Type of event" placeholder="Birthday, office lunch, celebration…" value={form.event_type} onChange={set("event_type")} />
            <Field label="Event date" placeholder="e.g. July 15, 2026" value={form.event_date} onChange={set("event_date")} />
            <Field label="Preferred pickup / delivery date & time" placeholder="e.g. July 15, 5:00 PM" value={form.preferred_datetime} onChange={set("preferred_datetime")} />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4a3524] mb-1">Pickup or delivery</label>
            <div className="grid grid-cols-2 gap-2">
              {["pickup", "delivery"].map((t) => (
                <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, fulfillment_type: t }))}
                  className={`rounded-xl py-2.5 text-sm font-medium capitalize transition-colors ${form.fulfillment_type === t ? "bg-[#9a4a2f] text-white" : "bg-[#efe3cd] text-[#4a3524]"}`}>{t}</button>
              ))}
            </div>
          </div>

          <TextArea label="Food selections" placeholder="Any preferences for the included dishes…" value={form.food_selections} onChange={set("food_selections")} />
          <TextArea label="Special requests" placeholder="Allergies, dietary needs, notes…" value={form.special_requests} onChange={set("special_requests")} />

          <button disabled={submitting} className="w-full rounded-xl bg-[#9a4a2f] hover:bg-[#853e26] disabled:opacity-60 text-white font-semibold py-3.5 transition-colors">
            {submitting ? "Sending…" : "Submit Catering Request"}
          </button>
          <p className="text-center text-xs text-[#8a7458]">We'll contact you to confirm details and payment (Zelle).</p>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pt-20 pb-24">
      <h1 className="font-heading text-4xl font-bold text-[#2c1e12]">Catering</h1>
      <p className="text-[#8a7458] mt-1">Hearty homemade Serbian catering for any occasion. Choose a package or build your own.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {packages.map((p) => (
          <div key={p.id} className="rounded-2xl bg-[#f6ece0] overflow-hidden shadow-sm flex flex-col">
            <img src={p.image} alt={p.name} className="w-full h-40 object-cover" loading="lazy" />
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-xl font-bold text-[#2c1e12]">{p.name}</h3>
                {p.people > 0 && <span className="flex items-center gap-1 text-xs font-semibold text-[#9a4a2f] bg-[#f7e4df] rounded-full px-2.5 py-1"><Users className="h-3 w-3" />{p.people}</span>}
              </div>
              <p className="text-xs text-[#8a7458] mt-0.5">{p.tagline}</p>
              <ul className="mt-3 space-y-1.5 flex-1">
                {p.includes.map((inc, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#6b5844]"><Check className="h-4 w-4 text-[#2c6e49] shrink-0 mt-0.5" />{inc}</li>
                ))}
              </ul>
              <div className="flex items-center justify-between mt-4">
                <span className="font-heading text-lg font-bold text-[#9a4a2f]">{p.custom ? "Custom quote" : `$${p.price.toFixed(2)}`}</span>
                <button onClick={() => open(p)} className="rounded-xl bg-[#9a4a2f] hover:bg-[#853e26] text-white text-sm font-semibold px-5 py-2 transition-colors">{p.custom ? "Build Your Own" : "Request"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, type = "text", ...props }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-[#4a3524] mb-1">{label}</label>
    <input type={type} {...props} className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40" />
  </div>
);

const TextArea = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-[#4a3524] mb-1">{label}</label>
    <textarea rows={3} {...props} className="w-full rounded-xl bg-[#f6ece0] border border-[#e6d8bf] px-3.5 py-2.5 text-[#2c1e12] focus:outline-none focus:ring-2 focus:ring-[#9a4a2f]/40" />
  </div>
);

export default Catering;
